/**
 * VerseSport — Real Data Phase 5 — Centralized Match Status Mapper.
 *
 * Single source of truth that normalizes every API-Football
 * `fixture.status.short` code into the VerseSport lifecycle and phase used
 * by the Match Header. Every UI component that displays official match
 * state MUST consume this mapper — no scattered status comparisons.
 *
 * API-Football status codes supported (per docs):
 *   TBD, NS, 1H, HT, 2H, ET, BT, P, FT, AET, PEN, SUSP, INT, PST, CANC,
 *   ABD, AWD, WO, LIVE.
 */

export type MatchLifecycle = "SCHEDULED" | "LIVE" | "FINISHED";

export type MatchPhaseCode =
  | "pre_match"
  | "first_half"
  | "halftime"
  | "second_half"
  | "extra_time"
  | "break_time"
  | "penalties"
  | "full_time"
  | "suspended"
  | "cancelled"
  | "unknown";

export interface NormalizedMatchStatus {
  /** Raw provider code (uppercased). */
  short: string | null;
  /** VerseSport lifecycle bucket. */
  lifecycle: MatchLifecycle;
  /** Fine-grained phase. */
  phase: MatchPhaseCode;
  /** True if the match is currently being played (regulation, ET, PEN). */
  isLive: boolean;
  /** True if the match has officially finished. */
  isFinished: boolean;
  /** True if the clock is paused for HT/BT. */
  isBreak: boolean;
  /** True if the match has been cancelled/postponed/abandoned/awarded/walkover. */
  isCancelled: boolean;
  /** True if the match is temporarily suspended or interrupted. */
  isSuspended: boolean;
  /** Short label for the pill: "LIVE" / "HT" / "FT" / "ET" / "PEN" / "SCHED" / "SUSP". */
  pillLabel: string;
}

const LIVE_CODES = new Set(["1H", "2H", "ET", "P", "LIVE"]);
const BREAK_CODES = new Set(["HT", "BT"]);
const FINISHED_CODES = new Set(["FT", "AET", "PEN"]);
const SUSPENDED_CODES = new Set(["SUSP", "INT"]);
const CANCELLED_CODES = new Set(["PST", "CANC", "ABD", "AWD", "WO"]);
const SCHEDULED_CODES = new Set(["TBD", "NS"]);

export function normalizeMatchStatus(
  short: string | null | undefined,
): NormalizedMatchStatus {
  const raw = (short ?? "").toUpperCase().trim() || null;

  let lifecycle: MatchLifecycle = "SCHEDULED";
  let phase: MatchPhaseCode = "unknown";
  let pillLabel = "SCHED";

  if (raw && SCHEDULED_CODES.has(raw)) {
    lifecycle = "SCHEDULED";
    phase = "pre_match";
    pillLabel = "SCHED";
  } else if (raw === "1H") {
    lifecycle = "LIVE"; phase = "first_half"; pillLabel = "LIVE";
  } else if (raw === "2H") {
    lifecycle = "LIVE"; phase = "second_half"; pillLabel = "LIVE";
  } else if (raw === "ET") {
    lifecycle = "LIVE"; phase = "extra_time"; pillLabel = "ET";
  } else if (raw === "P") {
    lifecycle = "LIVE"; phase = "penalties"; pillLabel = "PEN";
  } else if (raw === "LIVE") {
    lifecycle = "LIVE"; phase = "second_half"; pillLabel = "LIVE";
  } else if (raw === "HT") {
    lifecycle = "LIVE"; phase = "halftime"; pillLabel = "HT";
  } else if (raw === "BT") {
    lifecycle = "LIVE"; phase = "break_time"; pillLabel = "BT";
  } else if (raw === "FT") {
    lifecycle = "FINISHED"; phase = "full_time"; pillLabel = "FT";
  } else if (raw === "AET") {
    lifecycle = "FINISHED"; phase = "full_time"; pillLabel = "AET";
  } else if (raw === "PEN") {
    lifecycle = "FINISHED"; phase = "full_time"; pillLabel = "PEN";
  } else if (raw && SUSPENDED_CODES.has(raw)) {
    lifecycle = "LIVE"; phase = "suspended"; pillLabel = "SUSP";
  } else if (raw && CANCELLED_CODES.has(raw)) {
    lifecycle = "FINISHED"; phase = "cancelled"; pillLabel = raw;
  }

  return {
    short: raw,
    lifecycle,
    phase,
    isLive: lifecycle === "LIVE" && !BREAK_CODES.has(raw ?? ""),
    isFinished: lifecycle === "FINISHED",
    isBreak: raw ? BREAK_CODES.has(raw) : false,
    isCancelled: raw ? CANCELLED_CODES.has(raw) : false,
    isSuspended: raw ? SUSPENDED_CODES.has(raw) : false,
    pillLabel,
  };
}